import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Checkbox,
  CheckboxProps,
  Flex,
  Input,
  InputGroup,
  InputGroupProps,
  InputLeftElement,
  InputRightElement,
  Spacer,
} from "@chakra-ui/react";
import { useId } from "react";

import { RanaSelect, RanaSelectProps, RanaSelectValueType } from "../../rana-select";
import { BUTTON_STYLES } from "../../theme/styles/button-styles";

export interface RanaSearchProps extends InputGroupProps {
  placeholder: string;
  searchString: string;
  setSearchString: (searchString: string) => void;
}

export function RanaSearch({
  placeholder,
  searchString,
  setSearchString,
  ...rest
}: RanaSearchProps) {
  const id = useId();

  return (
    <InputGroup w="2xs" bg="transparent" {...rest}>
      <InputLeftElement>
        <SearchIcon />
      </InputLeftElement>
      <Input
        id={id}
        placeholder={placeholder}
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
      />
      {searchString && (
        <InputRightElement cursor="pointer">
          <CloseIcon color="gray.500" onClick={() => setSearchString("")} />
        </InputRightElement>
      )}
    </InputGroup>
  );
}

export interface RanaTableCheckboxProps extends CheckboxProps {
  label: string;
  checked: boolean;
  setChecked: (checked: boolean) => void;
}

export function RanaTableCheckbox({ label, checked, setChecked, ...rest }: RanaTableCheckboxProps) {
  const id = useId();

  return (
    <Checkbox
      id={id}
      {...BUTTON_STYLES.variants.tertiary}
      label={label}
      isChecked={checked}
      onChange={() => setChecked(!checked)}
      {...rest}
    >
      {label}
    </Checkbox>
  );
}

export interface TableFilterDropdownProps<Selected extends RanaSelectValueType> {
  label: string;
  selected: RanaSelectProps<Selected>["value"];
  setSelected: RanaSelectProps<Selected>["onChange"];
  options: RanaSelectProps<Selected>["options"];
}

export function TableFilterDropdown<Selected extends RanaSelectValueType>({
  label,
  selected,
  setSelected,
  options,
}: TableFilterDropdownProps<Selected>) {
  return (
    <RanaSelect
      minW={52}
      emptyLabel={label}
      options={options}
      onChange={(value) => setSelected(value)}
      value={selected}
    />
  );
}

export interface TableFiltersProps<Selected extends RanaSelectValueType> {
  search?: RanaSearchProps;
  checkboxes?: RanaTableCheckboxProps[];
  dropdowns?: TableFilterDropdownProps<Selected>[];
}

export function TableFilters<Selected extends RanaSelectValueType>({
  search,
  checkboxes,
  dropdowns,
}: TableFiltersProps<Selected>) {
  return (
    <Flex w="full" gap={4} mb={4}>
      {search && <RanaSearch {...search} />}

      {(checkboxes || dropdowns) && <Spacer />}

      {checkboxes?.map((checkbox) => (
        <RanaTableCheckbox key={checkbox.label} {...checkbox} />
      ))}

      {dropdowns?.map((dropdown, idx) => (
        <TableFilterDropdown key={idx} {...dropdown} />
      ))}
    </Flex>
  );
}
